import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileScan, Loader2, Plus, Trash2, Users, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { friendlyError } from "@/lib/errors";

export const PENDING_INTAKE_KEY = "nvc_pending_intake";

const RELATIONSHIP_OPTIONS = [
  { id: "spouse", label: "Spouse" },
  { id: "child", label: "Child" },
  { id: "parent", label: "Parent" },
  { id: "sibling", label: "Sibling" },
];

const READING_STEPS = [
  "Uploading your notice…",
  "Reading the pages…",
  "Finding your case number and family members…",
  "Almost done…",
];

/* ---------------- Upload zone (shared by dashboard + landing) ---------------- */
export function NvcUploadZone({ onExtracted, testId = "nvc-upload-zone" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    if (!busy) return;
    const t = setInterval(() => setStepIdx((i) => Math.min(i + 1, READING_STEPS.length - 1)), 6000);
    return () => clearInterval(t);
  }, [busy]);

  const pick = () => inputRef.current?.click();

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setStepIdx(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/nvc-notice/extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      if (!data?.extraction?.is_nvc_notice) {
        toast.warning("This doesn't look like an NVC notice — please check the details below carefully.");
      }
      onExtracted(data);
    } catch (err) {
      toast.error(friendlyError(err, "We couldn't read that document. Please try a clearer scan or PDF."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.webp"
        className="hidden"
        data-testid={`${testId}-input`}
        onChange={(e) => upload(e.target.files?.[0])}
      />
      {busy ? (
        <div className="flex items-center gap-3 text-sm py-2" data-testid={`${testId}-progress`}>
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--action)" }} />
          <span style={{ color: "var(--text-secondary)" }}>{READING_STEPS[stepIdx]}</span>
        </div>
      ) : (
        <button
          type="button"
          className="btn-primary flex items-center gap-2"
          onClick={pick}
          data-testid={`${testId}-btn`}
        >
          <FileScan className="w-4 h-4" /> Upload your NVC Notice
        </button>
      )}
    </div>
  );
}

/* ---------------- Review / confirm modal ---------------- */
export function NvcReviewModal({ intake, onClose, onCreated }) {
  const ex = intake.extraction || {};
  const [form, setForm] = useState({
    relationship: intake.suggested_relationship || "spouse",
    nvc_case_number: ex.nvc_case_number || "",
    invoice_id: ex.invoice_id || "",
    uscis_receipt_number: ex.uscis_receipt_number || "",
    visa_category: ex.visa_category || "",
    consulate: ex.consulate || "",
    priority_date: ex.priority_date || "",
    petitioner_first_name: ex.petitioner?.first_name || "",
    petitioner_last_name: ex.petitioner?.last_name || "",
  });
  const [applicants, setApplicants] = useState(
    (ex.applicants?.length ? ex.applicants : [{ first_name: "", last_name: "", dob: "", role: "principal", relationship_to_principal: "self" }])
      .map((a) => ({ ...a, _key: crypto.randomUUID() }))
  );
  const [creating, setCreating] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setApp = (idx, k) => (e) => {
    const v = e.target.value;
    setApplicants((list) => list.map((a, i) => (i === idx ? { ...a, [k]: v } : a)));
  };
  const addApplicant = () =>
    setApplicants((l) => [...l, { first_name: "", last_name: "", dob: "", role: "derivative", relationship_to_principal: "child", _key: crypto.randomUUID() }]);
  const removeApplicant = (idx) => setApplicants((l) => l.filter((_, i) => i !== idx));

  const confirm = async () => {
    const filled = applicants.filter((a) => `${a.first_name}${a.last_name}`.trim() !== "");
    if (!filled.length) {
      toast.error("Add at least one applicant.");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/nvc-notice/create-case", {
        intake_id: intake.intake_id,
        ...form,
        applicants: filled,
      });
      sessionStorage.removeItem(PENDING_INTAKE_KEY);
      toast.success("Your family case is ready — everyone from the notice is on it.");
      onCreated(data);
    } catch (err) {
      toast.error(friendlyError(err, "We couldn't create the case. Try again in a moment."));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      style={{ background: "rgba(26,46,36,0.45)" }}
      onClick={onClose}
    >
      <div
        className="card-surface p-6 sm:p-8 max-w-2xl w-full anim-in my-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="nvc-review-modal"
      >
        <div className="pill pill-ai mb-3">We read your notice</div>
        <h3 className="font-display text-2xl font-bold mb-1" data-testid="nvc-review-heading">
          We found {applicants.length} {applicants.length === 1 ? "applicant" : "family members"}
        </h3>
        <p className="text-sm mb-5" style={{ color: "var(--text-secondary)" }}>
          Check everything below — fix any misread names, then confirm to create the case with all details pre-filled.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
          <Field label="NVC case number" value={form.nvc_case_number} onChange={set("nvc_case_number")} testId="nvc-field-case-number" />
          <Field label="Invoice ID (IIN)" value={form.invoice_id} onChange={set("invoice_id")} testId="nvc-field-invoice-id" />
          <Field label="Visa category" value={form.visa_category} onChange={set("visa_category")} testId="nvc-field-visa-category" />
          <Field label="USCIS receipt #" value={form.uscis_receipt_number} onChange={set("uscis_receipt_number")} testId="nvc-field-receipt" />
          <Field label="Priority date" value={form.priority_date} onChange={set("priority_date")} placeholder="YYYY-MM-DD" testId="nvc-field-priority-date" />
          <Field label="Consulate" value={form.consulate} onChange={set("consulate")} testId="nvc-field-consulate" />
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <Field label="Petitioner first name" value={form.petitioner_first_name} onChange={set("petitioner_first_name")} testId="nvc-field-petitioner-first" />
          <Field label="Petitioner last name" value={form.petitioner_last_name} onChange={set("petitioner_last_name")} testId="nvc-field-petitioner-last" />
        </div>

        <div className="mb-4">
          <label className="field-label">Petitioning for</label>
          <div className="flex flex-wrap gap-2">
            {RELATIONSHIP_OPTIONS.map((r) => (
              <button
                key={r.id}
                type="button"
                data-testid={`nvc-rel-${r.id}`}
                onClick={() => setForm((f) => ({ ...f, relationship: r.id }))}
                className="px-3 py-1.5 rounded-full text-sm border transition-colors"
                style={{
                  background: form.relationship === r.id ? "var(--ai-bg)" : "#fff",
                  borderColor: form.relationship === r.id ? "var(--ai-border)" : "var(--border-stone)",
                  color: "var(--text-primary)",
                }}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-2 flex items-center justify-between">
          <label className="field-label mb-0">Applicants on this case</label>
          <button
            type="button"
            className="text-xs font-semibold flex items-center gap-1"
            style={{ color: "var(--action)" }}
            onClick={addApplicant}
            data-testid="nvc-add-applicant-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Add family member
          </button>
        </div>
        <div className="space-y-2 mb-4">
          {applicants.map((a, idx) => (
            <div
              key={a._key}
              className="grid grid-cols-[1fr_1fr_110px_auto] gap-2 items-center"
              data-testid={`nvc-applicant-row-${idx}`}
            >
              <input className="field-input" placeholder="First name" value={a.first_name} onChange={setApp(idx, "first_name")} data-testid={`nvc-applicant-${idx}-first`} />
              <input className="field-input" placeholder="Last name" value={a.last_name} onChange={setApp(idx, "last_name")} data-testid={`nvc-applicant-${idx}-last`} />
              <input className="field-input" placeholder="DOB" title="YYYY-MM-DD" value={a.dob} onChange={setApp(idx, "dob")} data-testid={`nvc-applicant-${idx}-dob`} />
              <div className="flex items-center gap-1">
                <span
                  className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded"
                  style={{ background: a.role === "principal" ? "var(--ai-bg)" : "var(--border-stone)", color: "var(--text-secondary)" }}
                >
                  {a.role === "principal" ? "Principal" : "Family"}
                </span>
                {a.role !== "principal" && (
                  <button type="button" onClick={() => removeApplicant(idx)} data-testid={`nvc-applicant-${idx}-remove`} aria-label="Remove applicant">
                    <Trash2 className="w-3.5 h-3.5" style={{ color: "var(--text-secondary)" }} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {ex.warnings?.length > 0 && (
          <div className="rounded-lg border p-3 mb-4 text-xs" style={{ borderColor: "var(--border-stone)", background: "#FDF8F0" }} data-testid="nvc-warnings">
            <div className="font-semibold mb-1" style={{ color: "var(--text-primary)" }}>Important instructions from your notice</div>
            <ul className="list-disc pl-4 space-y-0.5" style={{ color: "var(--text-secondary)" }}>
              {ex.warnings.slice(0, 4).map((w) => <li key={w}>{w}</li>)}
            </ul>
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <button type="button" className="btn-ghost text-sm" onClick={onClose} data-testid="nvc-review-cancel-btn">
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={confirm} disabled={creating} data-testid="nvc-review-confirm-btn">
            {creating ? "Creating your case…" : "Confirm & create case"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <input className="field-input" value={value} onChange={onChange} placeholder={placeholder} data-testid={testId} />
    </div>
  );
}

/* ---------------- Dashboard card ---------------- */
export function NvcNoticeDashboardCard() {
  const nav = useNavigate();
  const [intake, setIntake] = useState(null);

  useEffect(() => {
    const pending = sessionStorage.getItem(PENDING_INTAKE_KEY);
    if (!pending) return;
    api.get(`/nvc-notice/intake/${pending}`)
      .then(({ data }) => setIntake(data))
      .catch(() => sessionStorage.removeItem(PENDING_INTAKE_KEY));
  }, []);

  return (
    <div
      className="card-surface p-5 sm:p-6 mb-6 flex flex-col sm:flex-row sm:items-center gap-4"
      style={{ borderColor: "var(--ai-border)", background: "var(--ai-bg)" }}
      data-testid="nvc-notice-card"
    >
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <Users className="w-4 h-4" style={{ color: "var(--action)" }} />
          <h3 className="font-display font-bold text-lg" style={{ color: "var(--text-primary)" }}>
            Got your NVC Welcome Letter?
          </h3>
        </div>
        <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
          Upload it once — we'll read your case number, invoice ID, and every family member, then set up the whole family's case automatically.
        </p>
      </div>
      <NvcUploadZone testId="nvc-dashboard-upload" onExtracted={setIntake} />
      {intake && (
        <NvcReviewModal
          intake={intake}
          onClose={() => { setIntake(null); sessionStorage.removeItem(PENDING_INTAKE_KEY); }}
          onCreated={(c) => nav(`/case/${c.id}`)}
        />
      )}
    </div>
  );
}

/* ---------------- Landing page teaser (pre-signup) ---------------- */
export function NvcLandingTeaser() {
  const nav = useNavigate();
  const [result, setResult] = useState(null);

  const onExtracted = (data) => {
    sessionStorage.setItem(PENDING_INTAKE_KEY, data.intake_id);
    setResult(data);
  };

  const ex = result?.extraction;
  const principal = ex?.applicants?.find((a) => a.role === "principal");

  return (
    <section className="border-y" style={{ borderColor: "var(--border-stone)", background: "#FFFFFF" }}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="pill pill-ai mb-4" data-testid="nvc-teaser-pill">
              <FileScan className="w-3.5 h-3.5" /> New — instant case setup
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold mb-3" style={{ color: "var(--text-primary)" }} data-testid="nvc-teaser-heading">
              Have your NVC Welcome Letter? Start in 60 seconds.
            </h2>
            <p className="text-[15px] leading-relaxed max-w-xl mb-2" style={{ color: "var(--text-secondary)" }}>
              Upload the letter NVC sent you. We read your case number, invoice ID, and every family member on it — then build the whole family's DS-260 workspace for you. Nothing to retype.
            </p>
            <p className="text-xs flex items-center gap-1.5" style={{ color: "var(--text-secondary)" }}>
              <ShieldCheck className="w-3.5 h-3.5" /> Your document is processed securely and never shared.
            </p>
          </div>
          <div className="lg:col-span-5">
            {!result ? (
              <div className="card-surface p-6" data-testid="nvc-teaser-upload-card">
                <NvcUploadZone testId="nvc-landing-upload" onExtracted={onExtracted} />
                <p className="mt-3 text-[11px]" style={{ color: "var(--text-secondary)" }}>
                  PDF or photo, up to 8&nbsp;MB. Free to try — no account needed to see what we find.
                </p>
              </div>
            ) : (
              <div className="card-surface p-6" data-testid="nvc-teaser-result">
                <div className="pill pill-ai mb-3">We read your notice</div>
                <h3 className="font-display font-bold text-lg mb-2" style={{ color: "var(--text-primary)" }} data-testid="nvc-teaser-result-heading">
                  {ex?.is_nvc_notice
                    ? `Found ${ex.applicants.length} ${ex.applicants.length === 1 ? "applicant" : "family members"}${ex.nvc_case_number ? ` on case ${ex.nvc_case_number}` : ""}`
                    : "We read your document"}
                </h3>
                <ul className="text-sm space-y-1 mb-4" style={{ color: "var(--text-secondary)" }}>
                  {ex?.petitioner?.first_name && <li>Petitioner: {ex.petitioner.first_name} {ex.petitioner.last_name}</li>}
                  {principal && <li>Principal applicant: {principal.first_name} {principal.last_name}</li>}
                  {ex?.visa_category && <li>Category: {ex.visa_category}</li>}
                  {ex?.consulate && <li>Consulate: {ex.consulate}</li>}
                </ul>
                <button
                  type="button"
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  onClick={() => nav("/register")}
                  data-testid="nvc-teaser-register-btn"
                >
                  Create your free account to continue <ArrowRight className="w-4 h-4" />
                </button>
                <p className="mt-2 text-[11px] text-center" style={{ color: "var(--text-secondary)" }}>
                  We'll pick up right where you left off after you sign up.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
